/**
 * useConexion: el estado que muestra el indicador de conexión de la barra
 * superior (modo contingencia, paso 2 · oct-2026).
 *
 * Junta tres señales:
 *   - /api/ping cada 10 s (cada 5 s después de un fallo). Va con fetch directo,
 *     no por api/client: sin sesión y sin pasar por el log, que se llenaría.
 *   - Cualquier respuesta real de la API cuenta como "el servidor responde".
 *   - Los cambios con llave en curso (api.postIdempotente) y el estado del
 *     tiempo real (use-realtime).
 * La decisión de qué color mostrar es pura y está en src/lib/conexion.ts.
 *
 * Igual que use-realtime: un solo monitor por pestaña, que arranca con el
 * primer componente que lo usa y se detiene con el último.
 */
import { useEffect, useState } from 'react';
import { apiBaseUrl, onApiCall, onGuardado } from '../../api/client';
import { getEstadoTiempoReal, onEstadoTiempoReal } from './use-realtime';
import {
  estadoConexion, esFallaDeRed, type FotoConexion,
  PING_CADA_MS, PING_TRAS_FALLO_MS, PING_TIMEOUT_MS, FALLOS_PARA_SIN_CONEXION,
} from '../../lib/conexion';

// ---- Monitor compartido (módulo, no React) ---------------------------------
let ultimoOk: number | null = null;
let fallosSeguidos = 0;
let ultimoGuardadoOk: number | null = null;
const guardando = new Map<number, number>(); // id del cambio → cuándo empezó

const oyentes = new Set<() => void>();
let usuarios = 0;
let timerPing: ReturnType<typeof setTimeout> | null = null;
let pingEnCurso: Promise<void> | null = null;
let desenganchar: Array<() => void> = [];

function avisar() {
  for (const f of Array.from(oyentes)) {
    try { f(); } catch { /* un oyente no debe romper el monitor */ }
  }
}

function marcarOk() {
  ultimoOk = Date.now();
  fallosSeguidos = 0;
  avisar();
}

function marcarFallo() {
  fallosSeguidos++;
  avisar();
}

function programarPing() {
  if (timerPing) clearTimeout(timerPing);
  if (!usuarios) { timerPing = null; return; }
  timerPing = setTimeout(() => { void pingAhora(); }, fallosSeguidos ? PING_TRAS_FALLO_MS : PING_CADA_MS);
}

/** Pregunta ya. Si hay un ping en vuelo, espera ese en vez de mandar otro. */
export function pingAhora(): Promise<void> {
  if (pingEnCurso) return pingEnCurso;
  pingEnCurso = (async () => {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), PING_TIMEOUT_MS);
    try {
      const r = await fetch(`${apiBaseUrl}/api/ping`, { cache: 'no-store', signal: ctrl.signal });
      if (r.ok) marcarOk(); else marcarFallo();
    } catch {
      marcarFallo();
    } finally {
      clearTimeout(t);
      pingEnCurso = null;
      programarPing();
    }
  })();
  return pingEnCurso;
}

function arrancar() {
  const alVolverOCambiar = () => { void pingAhora(); };
  const alVolverVisible = () => { if (document.visibilityState === 'visible') void pingAhora(); };
  window.addEventListener('online', alVolverOCambiar);
  window.addEventListener('offline', alVolverOCambiar);
  document.addEventListener('visibilitychange', alVolverVisible);
  desenganchar = [
    () => window.removeEventListener('online', alVolverOCambiar),
    () => window.removeEventListener('offline', alVolverOCambiar),
    () => document.removeEventListener('visibilitychange', alVolverVisible),
    onApiCall((e) => {
      // Cualquier respuesta de la API (incluso un 400) prueba que responde.
      // Sin respuesta, o 502/503/504 del proxy: se confirma con un ping.
      if (esFallaDeRed(e.status)) void pingAhora();
      else marcarOk();
    }),
    onGuardado((e) => {
      if (e.fase === 'inicio') guardando.set(e.id, e.t);
      else if (e.fase === 'reintento') void pingAhora();
      else {
        guardando.delete(e.id);
        if (e.fase === 'ok') { ultimoGuardadoOk = e.t; ultimoOk = e.t; fallosSeguidos = 0; }
        else void pingAhora();
      }
      avisar();
    }),
    onEstadoTiempoReal(avisar),
  ];
  void pingAhora();
}

function detener() {
  for (const f of desenganchar) f();
  desenganchar = [];
  if (timerPing) clearTimeout(timerPing);
  timerPing = null;
}

function suscribir(fn: () => void): () => void {
  oyentes.add(fn);
  if (usuarios++ === 0) arrancar();
  return () => {
    oyentes.delete(fn);
    if (--usuarios === 0) detener();
  };
}

/** Sin conexión declarada (3 pings fallidos). Lectura directa, sin re-render. */
export function conexionCaida(): boolean {
  return fallosSeguidos >= FALLOS_PARA_SIN_CONEXION;
}

// ---- Copia local para la caja de contingencia (paso 3b) ---------------------
// La toma app/components/copia-local-contingencia.tsx; el indicador muestra
// cuándo fue la última, así el cajero sabe con qué datos va a trabajar.
let copiaTomadaEn: number | null = null;
// Pedidos de la caja de contingencia que siguen solo en este equipo (paso 3c).
let pedidosLocales = 0;

export function marcarPedidosLocales(n: number) {
  if (n === pedidosLocales) return;
  pedidosLocales = n;
  avisar();
}

export function marcarCopiaTomada(t: number) {
  copiaTomadaEn = t;
  avisar();
}

export function fotoConexion(): FotoConexion {
  return {
    ahora: Date.now(),
    ultimoOk,
    fallosSeguidos,
    guardandoDesde: Array.from(guardando.values()),
    ultimoGuardadoOk,
    pedidosLocales,
    tiempoReal: getEstadoTiempoReal(),
  };
}

// ---- Hook ------------------------------------------------------------------
export function useConexion() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const repintar = () => setTick(n => n + 1);
    const off = suscribir(repintar);
    // El estado también cambia solo con el tiempo (un "sí" que envejece, un
    // guardado que empieza a tardar): se recalcula cada segundo.
    const reloj = setInterval(repintar, 1000);
    return () => { off(); clearInterval(reloj); };
  }, []);
  const foto = fotoConexion();
  return {
    foto,
    estado: estadoConexion(foto),
    pendientes: foto.guardandoDesde.length,
    copiaTomadaEn,
    comprobarAhora: pingAhora,
  };
}
